/**
 * Router built from module manifests (discovered modules plus the shell's
 * built-ins). Each manifest route becomes a lazily loaded TanStack route
 * carrying its manifest data as `staticData`, so the shell knows the active
 * route's office-scope mode, permission and entitlement.
 *
 * The office selector lives in the URL (`?office=`) and is kept on every
 * navigation by the root route's search middleware.
 */
import type { ModuleManifest, RouteSpec } from '@velora/contracts';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  retainSearchParams,
  type AnyRoute,
} from '@tanstack/react-router';
import { ShellRoot } from './layout/ShellRoot.js';
import { NotFoundPage } from './layout/states.js';
import type { WebModule } from './module.js';
import { OFFICE_PARAM } from './office/scope.js';
import { PIN_PARAM, POP_PARAM, readPopout } from './windows/popout.js';

export interface ShellRouteData {
  moduleId: string;
  route: RouteSpec;
  entitlementKey: string | null;
  placeholder: boolean;
}

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    shell?: ShellRouteData;
  }
}

export interface RootSearch {
  office?: string;
  /** Pop-out window of this module (windows/popout.ts). */
  pop?: string;
  /** Pop-out locked to its patient (1) or following the selection (0 or absent). */
  pin?: number;
}

/** The path the app opens on: the first nav item of the first nav group. */
export function homePathOf(modules: readonly WebModule[]): string | undefined {
  const items = modules.flatMap(({ manifest }) => manifest.nav.map((n) => ({ n, manifest })));
  const order = ['dashboard', 'schedule'];
  items.sort(
    (a, b) =>
      (order.indexOf(a.n.group) + 1 || 99) - (order.indexOf(b.n.group) + 1 || 99) ||
      a.n.order - b.n.order,
  );
  const first = items[0];
  return first?.manifest.routes.find((r) => r.id === first.n.routeId)?.path;
}

export function buildRouter(
  modules: readonly WebModule[],
  isPlaceholder: (m: WebModule) => boolean = () => false,
) {
  const rootRoute = createRootRoute({
    component: () => <ShellRoot modules={modules} />,
    notFoundComponent: NotFoundPage,
    validateSearch: (search: Record<string, unknown>): RootSearch => {
      const out: RootSearch = {};
      if (typeof search[OFFICE_PARAM] === 'string') out.office = search[OFFICE_PARAM] as string;
      // A pop-out stays a pop-out on every navigation inside it.
      const pop = readPopout(search);
      if (pop) {
        out.pop = pop.key;
        // 0 (follow) is kept too: the middleware re-adds a missing key, so
        // Follow is written as pin=0 rather than by removing pin.
        if (pop.pinned) out.pin = 1;
        else if (search[PIN_PARAM] === '0' || search[PIN_PARAM] === 0) out.pin = 0;
      }
      return out;
    },
    search: { middlewares: [retainSearchParams([OFFICE_PARAM, POP_PARAM, PIN_PARAM])] },
  });

  const routeData = (m: WebModule, route: RouteSpec, manifest: ModuleManifest): ShellRouteData => ({
    moduleId: manifest.id,
    route,
    entitlementKey:
      route.entitlementKey !== undefined ? route.entitlementKey : manifest.entitlementKey,
    placeholder: isPlaceholder(m),
  });

  const moduleRoutes: AnyRoute[] = modules.flatMap((m) =>
    m.manifest.routes.map((route) => {
      const loader = m.pages[route.id];
      if (!loader) throw new Error(`module "${m.manifest.id}" route "${route.id}" has no page`);
      return createRoute({
        getParentRoute: () => rootRoute,
        path: route.path,
        component: lazyRouteComponent(loader),
        staticData: { shell: routeData(m, route, m.manifest) },
      });
    }),
  );

  const homePath = homePathOf(modules);
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: ({ search }) => {
      if (homePath && homePath !== '/') throw redirect({ to: homePath, search });
    },
  });

  return createRouter({
    routeTree: rootRoute.addChildren([indexRoute, ...moduleRoutes]),
    defaultPreload: 'intent',
  });
}
